/* eslint-disable no-unused-vars */
import React from 'react';
import axios from 'axios';
import moment from 'moment';
import {Label} from 'react-bootstrap';
import ReactRichTextEditor, {EditorValue} from 'react-rte';
import {convertToRaw, EditorState, Modifier} from 'draft-js';
import katex from 'katex';
import XLSX from 'xlsx';
import {formValueSelector} from 'redux-form';
import {connect} from 'react-redux';
import {reduxFormWrapper} from './form';
import {isEmpty} from './validation';
import draftToHtml from 'draftjs-to-html';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

export const isTypedArray = v => (
    v instanceof Int8Array ||
    v instanceof Uint8Array ||
    v instanceof Uint8ClampedArray ||
    v instanceof Int16Array ||
    v instanceof Uint16Array ||
    v instanceof Int32Array ||
    v instanceof Uint32Array ||
    v instanceof Float32Array ||
    v instanceof Float64Array
);

export const isKeyCollection = v => (
    v instanceof Map ||
    v instanceof Set ||
    v instanceof WeakMap ||
    v instanceof WeakSet
);

export const isObject = v => (
    !!v && !(
        v instanceof Array ||
        v instanceof Function ||
        v instanceof Number ||
        v instanceof String ||
        v instanceof RegExp ||
        v instanceof Promise ||
        v instanceof Date ||
        v instanceof DataView ||
        typeof v === 'string' ||
        typeof v === 'number' ||
        typeof v === 'boolean' ||
        typeof v === 'symbol' ||
        isTypedArray(v) ||
        isKeyCollection(v)
    )
);

export const toCamelCase = v => v.replace(/\B_[a-z0-9]/g, m => m[1].toUpperCase());

export const snakeToCamel = data => {
    //console.log(data);
    if (Array.isArray(data)) {
        const ret = [];
        for (let item of data) {
            ret.push(snakeToCamel(item));
        }
        return ret;
    } else if (isObject(data)) {
        let ret = {};
        for (let [k, v] of Object.entries(data)) {
            ret[toCamelCase(k)] = snakeToCamel(v);
        }
        return ret;
    }
    //console.log(data);
    return data;
};

export const toSnakeCase = v => v.replace(/\B[a-z0-9][A-Z]/g, m => m[0] + '_' + m[1].toLowerCase());

export const camelToSnake = data => {
    if (Array.isArray(data)) {
        const ret = [];
        for (let item of data) {
            ret.push(camelToSnake(item));
        }
        return ret;
    } else if (isObject(data)) {
        let ret = {};
        for (let [k, v] of Object.entries(data)) {
            ret[toSnakeCase(k)] = camelToSnake(v);
        }
        return ret;
    }
    return data;
};

export const excludeObjNullValue = (data, excludeUndefined = false) => {
    // Shallow operation
    const ret = {};
    for (let [k, v] of Object.entries(data)) {
        if (excludeUndefined && typeof v === 'undefined') {
            continue;
        }
        if (v !== null) {
            ret[k] = v;
        }
    }
    return ret;
};

export const isObjectShallowEqual = (obj1, obj2) => (
    Object.keys(obj1).length === Object.keys(obj2).length && (
        Object.keys(obj1).reduce((isEqual, k) => (
            isEqual && (obj1[k] === obj2[k])
        ), true)
    )
);

export const usStateList = [
    {acronym: 'AL', name: 'Alabama'},
    {acronym: 'AK', name: 'Alaska'},
    {acronym: 'AZ', name: 'Arizona'},
    {acronym: 'AR', name: 'Arkansas'},
    {acronym: 'CA', name: 'California'},
    {acronym: 'CO', name: 'Colorado'},
    {acronym: 'CT', name: 'Connecticut'},
    {acronym: 'DE', name: 'Delaware'},
    {acronym: 'DC', name: 'District Of Columbia'},
    {acronym: 'FL', name: 'Florida'},
    {acronym: 'GA', name: 'Georgia'},
    {acronym: 'HI', name: 'Hawaii'},
    {acronym: 'ID', name: 'Idaho'},
    {acronym: 'IL', name: 'Illinois'},
    {acronym: 'IN', name: 'Indiana'},
    {acronym: 'IA', name: 'Iowa'},
    {acronym: 'KS', name: 'Kansas'},
    {acronym: 'KY', name: 'Kentucky'},
    {acronym: 'LA', name: 'Louisiana'},
    {acronym: 'ME', name: 'Maine'},
    {acronym: 'MD', name: 'Maryland'},
    {acronym: 'MA', name: 'Massachusetts'},
    {acronym: 'MI', name: 'Michigan'},
    {acronym: 'MN', name: 'Minnesota'},
    {acronym: 'MS', name: 'Mississippi'},
    {acronym: 'MO', name: 'Missouri'},
    {acronym: 'MT', name: 'Montana'},
    {acronym: 'NE', name: 'Nebraska'},
    {acronym: 'NV', name: 'Nevada'},
    {acronym: 'NH', name: 'New Hampshire'},
    {acronym: 'NJ', name: 'New Jersey'},
    {acronym: 'NM', name: 'New Mexico'},
    {acronym: 'NY', name: 'New York'},
    {acronym: 'NC', name: 'North Carolina'},
    {acronym: 'ND', name: 'North Dakota'},
    {acronym: 'OH', name: 'Ohio'},
    {acronym: 'OK', name: 'Oklahoma'},
    {acronym: 'OR', name: 'Oregon'},
    {acronym: 'PA', name: 'Pennsylvania'},
    {acronym: 'PR', name: 'Puerto Rico'},
    {acronym: 'RI', name: 'Rhode Island'},
    {acronym: 'SC', name: 'South Carolina'},
    {acronym: 'SD', name: 'South Dakota'},
    {acronym: 'TN', name: 'Tennessee'},
    {acronym: 'TX', name: 'Texas'},
    {acronym: 'UT', name: 'Utah'},
    {acronym: 'VT', name: 'Vermont'},
    {acronym: 'VA', name: 'Virginia'},
    {acronym: 'WA', name: 'Washington'},
    {acronym: 'WV', name: 'West Virginia'},
    {acronym: 'WI', name: 'Wisconsin'},
    {acronym: 'WY', name: 'Wyoming'},
];

export const canadaProvinceList = [
    {acronym: 'AB', name: 'Alberta'},
    {acronym: 'BC', name: 'British Columbia'},
    {acronym: 'MB', name: 'Manitoba'},
    {acronym: 'NB', name: 'New Brunswick'},
    {acronym: 'NL', name: 'Newfoundland and Labrador'},
    {acronym: 'NS', name: 'Nova Scotia'},
    {acronym: 'NT', name: 'Northwest Territories'},
    {acronym: 'NU', name: 'Nunavut'},
    {acronym: 'ON', name: 'Ontario'},
    {acronym: 'PE', name: 'Prince Edward Island'},
    {acronym: 'QC', name: 'Québec'},
    {acronym: 'SK', name: 'Saskatchewan'},
    {acronym: 'YT', name: 'Yukon'},
];

export const countryList = [
    {acronym: 'US', name: 'United States'},
    {acronym: 'CA', name: 'Canada'},
    {acronym: 'MX', name: 'Mexico'},
    {acronym: 'AF', name: 'Afghanistan'},
    {acronym: 'AX', name: 'Åland Islands'},
    {acronym: 'AL', name: 'Albania'},
    {acronym: 'DZ', name: 'Algeria'},
    {acronym: 'AS', name: 'American Samoa'},
    {acronym: 'AD', name: 'Andorra'},
    {acronym: 'AO', name: 'Angola'},
    {acronym: 'AI', name: 'Anguilla'},
    {acronym: 'AG', name: 'Antigua and Barbuda'},
    {acronym: 'AR', name: 'Argentina'},
    {acronym: 'AM', name: 'Armenia'},
    {acronym: 'AW', name: 'Aruba'},
    {acronym: 'AU', name: 'Australia'},
    {acronym: 'AT', name: 'Austria'},
    {acronym: 'AZ', name: 'Azerbaijan'},
    {acronym: 'BS', name: 'Bahamas'},
    {acronym: 'BH', name: 'Bahrain'},
    {acronym: 'BD', name: 'Bangladesh'},
    {acronym: 'BB', name: 'Barbados'},
    {acronym: 'BY', name: 'Belarus'},
    {acronym: 'BE', name: 'Belgium'},
    {acronym: 'BZ', name: 'Belize'},
    {acronym: 'BJ', name: 'Benin'},
    {acronym: 'BM', name: 'Bermuda'},
    {acronym: 'BT', name: 'Bhutan'},
    {acronym: 'BO', name: 'Bolivia'},
    {acronym: 'BA', name: 'Bosnia and Herzegovina'},
    {acronym: 'BW', name: 'Botswana'},
    {acronym: 'BR', name: 'Brazil'},
    {acronym: 'BN', name: 'Brunei'},
    {acronym: 'BG', name: 'Bulgaria'},
    {acronym: 'BF', name: 'Burkina Faso'},
    {acronym: 'BI', name: 'Burundi'},
    {acronym: 'KH', name: 'Cambodia'},
    {acronym: 'CM', name: 'Cameroon'},
    {acronym: 'CV', name: 'Cape Verde'},
    {acronym: 'KY', name: 'Cayman Islands'},
    {acronym: 'CF', name: 'Central African Republic'},
    {acronym: 'TD', name: 'Chad'},
    {acronym: 'CL', name: 'Chile'},
    {acronym: 'CN', name: 'China'},
    {acronym: 'CO', name: 'Colombia'},
    {acronym: 'KM', name: 'Comoros'},
    {acronym: 'CG', name: 'Congo'},
    {acronym: 'CD', name: 'Congo, The Democratic Republic of the'},
    {acronym: 'CK', name: 'Cook Islands'},
    {acronym: 'CR', name: 'Costa Rica'},
    {acronym: 'CI', name: 'Cote D\'Ivoire'},
    {acronym: 'HR', name: 'Croatia'},
    {acronym: 'CU', name: 'Cuba'},
    {acronym: 'UC', name: 'Curacao'},
    {acronym: 'CY', name: 'Cyprus'},
    {acronym: 'CZ', name: 'Czech Republic'},
    {acronym: 'DX', name: 'Dhekelia'},
    {acronym: 'DK', name: 'Denmark'},
    {acronym: 'DJ', name: 'Djibouti'},
    {acronym: 'DM', name: 'Dominica'},
    {acronym: 'DO', name: 'Dominican Republic'},
    {acronym: 'EC', name: 'Ecuador'},
    {acronym: 'EG', name: 'Egypt'},
    {acronym: 'SV', name: 'El Salvador'},
    {acronym: 'GQ', name: 'Equatorial Guinea'},
    {acronym: 'ER', name: 'Eritrea'},
    {acronym: 'EE', name: 'Estonia'},
    {acronym: 'WZ', name: 'Eswatini'},
    {acronym: 'ET', name: 'Ethiopia'},
    {acronym: 'FK', name: 'Falkland Islands (Malvinas)'},
    {acronym: 'FO', name: 'Faroe Islands'},
    {acronym: 'FJ', name: 'Fiji'},
    {acronym: 'FI', name: 'Finland'},
    {acronym: 'FR', name: 'France'},
    {acronym: 'GF', name: 'French Guiana'},
    {acronym: 'PF', name: 'French Polynesia'},
    {acronym: 'TF', name: 'French Southern Territories'},
    {acronym: 'GA', name: 'Gabon'},
    {acronym: 'GM', name: 'Gambia'},
    {acronym: 'GE', name: 'Georgia'},
    {acronym: 'DE', name: 'Germany'},
    {acronym: 'GH', name: 'Ghana'},
    {acronym: 'GI', name: 'Gibraltar'},
    {acronym: 'GR', name: 'Greece'},
    {acronym: 'GL', name: 'Greenland'},
    {acronym: 'GD', name: 'Grenada'},
    {acronym: 'GP', name: 'Guadeloupe'},
    {acronym: 'GU', name: 'Guam'},
    {acronym: 'GT', name: 'Guatemala'},
    {acronym: 'GG', name: 'Guernsey'},
    {acronym: 'GN', name: 'Guinea'},
    {acronym: 'GW', name: 'Guinea-Bissau'},
    {acronym: 'GY', name: 'Guyana'},
    {acronym: 'HT', name: 'Haiti'},
    {acronym: 'VA', name: 'Holy See (Vatican City State)'},
    {acronym: 'HN', name: 'Honduras'},
    {acronym: 'HK', name: 'Hong Kong'},
    {acronym: 'HU', name: 'Hungary'},
    {acronym: 'IS', name: 'Iceland'},
    {acronym: 'IN', name: 'India'},
    {acronym: 'ID', name: 'Indonesia'},
    {acronym: 'IR', name: 'Iran'},
    {acronym: 'IQ', name: 'Iraq'},
    {acronym: 'IE', name: 'Ireland'},
    {acronym: 'IM', name: 'Isle of Man'},
    {acronym: 'IL', name: 'Israel'},
    {acronym: 'IT', name: 'Italy'},
    {acronym: 'JM', name: 'Jamaica'},
    {acronym: 'JP', name: 'Japan'},
    {acronym: 'JE', name: 'Jersey'},
    {acronym: 'JO', name: 'Jordan'},
    {acronym: 'KZ', name: 'Kazakhstan'},
    {acronym: 'KE', name: 'Kenya'},
    {acronym: 'KI', name: 'Kiribati'},
    {acronym: 'KP', name: 'Korea, Democratic People\'S Republic of'},
    {acronym: 'KR', name: 'Korea, Republic of'},
    {acronym: 'KW', name: 'Kuwait'},
    {acronym: 'KG', name: 'Kyrgyzstan'},
    {acronym: 'LA', name: 'Lao People\'S Democratic Republic'},
    {acronym: 'LV', name: 'Latvia'},
    {acronym: 'LB', name: 'Lebanon'},
    {acronym: 'LS', name: 'Lesotho'},
    {acronym: 'LR', name: 'Liberia'},
    {acronym: 'LY', name: 'Libya'},
    {acronym: 'LI', name: 'Liechtenstein'},
    {acronym: 'LT', name: 'Lithuania'},
    {acronym: 'LU', name: 'Luxembourg'},
    {acronym: 'MO', name: 'Macao'},
    {acronym: 'MK', name: 'Macedonia'},
    {acronym: 'MG', name: 'Madagascar'},
    {acronym: 'MW', name: 'Malawi'},
    {acronym: 'MY', name: 'Malaysia'},
    {acronym: 'MV', name: 'Maldives'},
    {acronym: 'ML', name: 'Mali'},
    {acronym: 'MT', name: 'Malta'},
    {acronym: 'MH', name: 'Marshall Islands'},
    {acronym: 'MQ', name: 'Martinique'},
    {acronym: 'MR', name: 'Mauritania'},
    {acronym: 'MU', name: 'Mauritius'},
    {acronym: 'YT', name: 'Mayotte'},
    {acronym: 'FM', name: 'Micronesia, Federated States of'},
    {acronym: 'MD', name: 'Moldova, Republic of'},
    {acronym: 'MC', name: 'Monaco'},
    {acronym: 'MN', name: 'Mongolia'},
    {acronym: 'ME', name: 'Montenegro'},
    {acronym: 'MS', name: 'Montserrat'},
    {acronym: 'MA', name: 'Morocco'},
    {acronym: 'MZ', name: 'Mozambique'},
    {acronym: 'MM', name: 'Myanmar'},
    {acronym: 'NA', name: 'Namibia'},
    {acronym: 'NR', name: 'Nauru'},
    {acronym: 'NP', name: 'Nepal'},
    {acronym: 'NL', name: 'Netherlands'},
    {acronym: 'AN', name: 'Netherlands Antilles'},
    {acronym: 'NC', name: 'New Caledonia'},
    {acronym: 'NZ', name: 'New Zealand'},
    {acronym: 'NI', name: 'Nicaragua'},
    {acronym: 'NE', name: 'Niger'},
    {acronym: 'NG', name: 'Nigeria'},
    {acronym: 'NF', name: 'Norfolk Island'},
    {acronym: 'MP', name: 'Northern Mariana Islands'},
    {acronym: 'NO', name: 'Norway'},
    {acronym: 'OM', name: 'Oman'},
    {acronym: 'PK', name: 'Pakistan'},
    {acronym: 'PW', name: 'Palau'},
    {acronym: 'PA', name: 'Panama'},
    {acronym: 'PG', name: 'Papua New Guinea'},
    {acronym: 'PY', name: 'Paraguay'},
    {acronym: 'PE', name: 'Peru'},
    {acronym: 'PH', name: 'Philippines'},
    {acronym: 'PL', name: 'Poland'},
    {acronym: 'PT', name: 'Portugal'},
    {acronym: 'PR', name: 'Puerto Rico'},
    {acronym: 'QA', name: 'Qatar'},
    {acronym: 'RE', name: 'Reunion'},
    {acronym: 'RO', name: 'Romania'},
    {acronym: 'RU', name: 'Russian Federation'},
    {acronym: 'RW', name: 'Rwanda'},
    {acronym: 'SH', name: 'Saint Helena'},
    {acronym: 'KN', name: 'Saint Kitts and Nevis'},
    {acronym: 'LC', name: 'Saint Lucia'},
    {acronym: 'VC', name: 'Saint Vincent and the Grenadines'},
    {acronym: 'WS', name: 'Samoa'},
    {acronym: 'SM', name: 'San Marino'},
    {acronym: 'ST', name: 'Sao Tome and Principe'},
    {acronym: 'SA', name: 'Saudi Arabia'},
    {acronym: 'SN', name: 'Senegal'},
    {acronym: 'RS', name: 'Serbia'},
    {acronym: 'SC', name: 'Seychelles'},
    {acronym: 'SL', name: 'Sierra Leone'},
    {acronym: 'SG', name: 'Singapore'},
    {acronym: 'SK', name: 'Slovakia'},
    {acronym: 'SI', name: 'Slovenia'},
    {acronym: 'SB', name: 'Solomon Islands'},
    {acronym: 'SO', name: 'Somalia'},
    {acronym: 'ZA', name: 'South Africa'},
    {acronym: 'ES', name: 'Spain'},
    {acronym: 'LK', name: 'Sri Lanka'},
    {acronym: 'SD', name: 'Sudan'},
    {acronym: 'SR', name: 'Suriname'},
    {acronym: 'SZ', name: 'Swaziland'},
    {acronym: 'SE', name: 'Sweden'},
    {acronym: 'CH', name: 'Switzerland'},
    {acronym: 'SY', name: 'Syrian Arab Republic'},
    {acronym: 'TW', name: 'Taiwan, Province of China'},
    {acronym: 'TJ', name: 'Tajikistan'},
    {acronym: 'TZ', name: 'Tanzania, United Republic of'},
    {acronym: 'TH', name: 'Thailand'},
    {acronym: 'TL', name: 'Timor-Leste'},
    {acronym: 'TG', name: 'Togo'},
    {acronym: 'TO', name: 'Tonga'},
    {acronym: 'TT', name: 'Trinidad and Tobago'},
    {acronym: 'TN', name: 'Tunisia'},
    {acronym: 'TR', name: 'Turkey'},
    {acronym: 'TM', name: 'Turkmenistan'},
    {acronym: 'TC', name: 'Turks and Caicos Islands'},
    {acronym: 'TV', name: 'Tuvalu'},
    {acronym: 'UG', name: 'Uganda'},
    {acronym: 'UA', name: 'Ukraine'},
    {acronym: 'AE', name: 'United Arab Emirates'},
    {acronym: 'GB', name: 'United Kingdom'},
    {acronym: 'UY', name: 'Uruguay'},
    {acronym: 'UZ', name: 'Uzbekistan'},
    {acronym: 'VU', name: 'Vanuatu'},
    {acronym: 'VE', name: 'Venezuela'},
    {acronym: 'VN', name: 'Vietnam'},
    {acronym: 'VG', name: 'Virgin Islands, British'},
    {acronym: 'VI', name: 'Virgin Islands, U.S.'},
    {acronym: 'WF', name: 'Wallis and Futuna'},
    {acronym: 'EH', name: 'Western Sahara'},
    {acronym: 'YE', name: 'Yemen'},
    {acronym: 'ZM', name: 'Zambia'},
    {acronym: 'ZW', name: 'Zimbabwe'}
];

export const usStatesAcronymToNameMap = usStateList.reduce((obj, state) => (
    {...obj, [state.acronym]: state.name}
), {});

export const usStatesNameToAcronymMap = usStateList.reduce((obj, state) => (
    {...obj, [state.name]: state.acronym}
), {});

// new for canadian provinces and countries
export const canadaProvinceAcronymToNameMap = canadaProvinceList.reduce((obj, state) => (
    {...obj, [state.acronym]: state.name}
), {});

export const canadaProvinceNameToAcronymMap = canadaProvinceList.reduce((obj, state) => (
    {...obj, [state.name]: state.acronym}
), {});

export const countryAcronymToNameMap = countryList.reduce((obj, country) => (
    {...obj, [country.acronym]: country.name}
), {});

export const countryNameToAcronymMap = countryList.reduce((obj, country) => (
    {...obj, [country.name]: country.acronym}
), {});
// export const usStatesOptions = usStateList.map((stateObj)=>({text:stateObj.name, value:stateObj.acronym}));

export const parseIfIsNumber = (v, action = parseInt) => {
    if (Array.isArray(v) || isObject(v)) {
        return;
    }
    const parsed = action(v);
    return isNaN(parsed) ? undefined : parsed;
};

export const isFormFieldValid = validations => Object.values(validations).reduce((isValid, v) => isValid && v, true);


export function validateForm() {
    const touchedKeys = Object.keys(this.state).filter(v => v.endsWith('Touched'));
    const newState = touchedKeys.reduce((stateObj, touchedKey) => ({
        ...stateObj, [touchedKey]: true
    }), {});
    return new Promise(resolve => {
        this.setState(newState, () => {
            const fieldsValidation = this.getFieldsValidation(this.state, this.props);
            const isFormValid = Object.values(fieldsValidation).reduce((isValid, fieldValidations) => (
                isValid && isFormFieldValid(fieldValidations)
            ), true);
            resolve(isFormValid);
        });
    });
}

export const formatIfIsPhoneNumber = s => {
    if (typeof s !== 'string' || isEmpty(s)) {
        return s;
    }
    const match = s.match(/^(\d{3})(\d{3})(\d{4})$/);
    return match !== null ?
        `(${match[1]}) ${match[2]}-${match[3]}` :
        s;
};

export const calcTableShowingEntriesInfo = (itemsPerPage, currentPage, totalCount) => (
    `Showing ${Math.min((currentPage - 1) * itemsPerPage + 1, totalCount)} ` +
    `to ${Math.min(currentPage * itemsPerPage, totalCount)} ` +
    `of ${totalCount} ${totalCount === 1 ? 'entry' : 'entries'}`
);

export function getUserFriendlyRoleName(role) {
    if (role === 'pitsco_admin') return 'Pitsco Admin';
    else if (role === 'district_admin') return 'District Admin';
    else if (role === 'school_admin') return 'School Admin';
    else if (role === 'institution_admin') return 'Institution Admin';
    else if (role === 'teacher') return 'Teacher';
    else if (role === 'student') return 'Student';
    else throw Error(`${role} is an unknown role`);
}

// Formatting the user role field in the table
export function roleLabelFormatter(role) {
    const roleName = getUserFriendlyRoleName(role);
    if (role === 'teacher') return `<span class="label label-danger">${roleName}</span>`;
    else if (role === 'student') return `<span class="label label-default">${roleName}</span>`;
    else throw Error(`${role} is an unknown role`);
}

moment.locale('en');
const backendDatetimeFormat = moment.ISO_8601;
const datetimeFormat = 'MM/DD/YYYY h:mmA';
const dateFormat = 'MM/DD/YYYY';

export const momentDatetime = (datetime, isUtc = true) => {
    let parsedDatetime;
    if (moment.isMoment(datetime)) {
        parsedDatetime = datetime;
    } else if (datetime instanceof Date) {
        parsedDatetime = moment(datetime);
    } else if (!isEmpty(datetime) && typeof datetime === 'string') {
        parsedDatetime = moment(datetime, backendDatetimeFormat);
    } else {
        throw TypeError(`Invalid type of datetime: ${datetime} (type: ${typeof datetime})`);
    }
    return isUtc ? parsedDatetime.utc(parsedDatetime).local() : parsedDatetime.local();
};

export const formatDatetime = (datetime, isUtc = true, format = datetimeFormat) => (
    moment.isMoment(datetime) || datetime instanceof Date || (!isEmpty(datetime) && typeof datetime === 'string') ?
        momentDatetime(datetime, isUtc).format(format) :
        ''
);

export const formatTimeAgo = (datetime) => !datetime ? '' : momentDatetime(datetime).fromNow();

export const formatDate = (datetime, isUtc = true, format = dateFormat) => formatDatetime(datetime, isUtc, format);

export const dateStringToMomentDatetime = (date, isUtc = false) => {
    const datetime = moment(date, 'YYYY-MM-DD');
    if (isUtc) {
        return datetime.utc(datetime).local();
    }
    return datetime.local();
};

export const capfirst = str => {
    if (typeof str !== 'string' || str.length <= 1) {
        return str;
    }
    return str[0].toUpperCase() + str.slice(1);
};

export const buildUserFullName = (firstNameOrUserObj, lastName) => {
    if (isObject(firstNameOrUserObj) && firstNameOrUserObj.firstName && firstNameOrUserObj.lastName) {
        return buildUserFullName(firstNameOrUserObj.firstName, firstNameOrUserObj.lastName);
    }
    if (!firstNameOrUserObj || !lastName) {
        return '';
    }
    return `${capfirst(firstNameOrUserObj)} ${capfirst(lastName)}`;
};

export const buildUserFullNameWithUsername = (user) => {
    if (!user.firstName || !user.lastName || !user.username) {
        return '';
    }
    return `${capfirst(user.firstName)} ${capfirst(user.lastName)} (${user.username})`;
};

export const findRemoveArrayElement = (arr, cmpfn) => {
    const idx = arr.findIndex(cmpfn);
    if (idx !== -1) {
        arr.splice(idx, 1);
    }
    return arr;
};

export const makeEditorValue = input => {
    if (input instanceof EditorValue) {
        return input;
    }
    if (isEmpty(input)) {
        return ReactRichTextEditor.createEmptyValue();
    }
    if (typeof input === 'string') {
        return ReactRichTextEditor.createValueFromString(input, 'html');
    }
    throw Error(`Invalid argument: ${input}`);
};

export const isEditorValueStringEmpty = editorValueString => editorValueString === '<p><br></p>';

export const resourceTypes = {
    activity: 'Activity',
    ipl: 'IPL',
    lesson: 'Lesson',
    z_space: 'Z-Space',
};

export const messageTypes = {
    True: 'Archived',
    False: 'Active',
};

export const ideaTypes = {
    pending: 'Pending',
    active: 'Active',
    archived: 'Archived',
    approved: 'Approved',
    released: 'Released',
};

export const moduleTypes = {
    module: 'Module',
    quest: 'Quest',
    mathMod: 'Math Module',
};

export const emptyStrIfIsEmpty = v => isEmpty(v) ? '' : v;

export const nullIfIsEmpty = v => isEmpty(v) ? null : v;

export function checkAndSetFormData(nextProps) {
    const {formData} = nextProps;
    if (!isObjectShallowEqual(this.state.formData, formData)) {
        this.setState({
            formData,
        });
    }
}

export const getDestinationType = (type) => {
    switch (type) {
        case 'expedition':
        case 'expeditions':
            return 'destination';
        case 'mission':
        case 'missions':
            return 'interval';
        default:
            return type;
    }
};

export const getTaskType = (type) => {
    switch (type) {
        case 'expedition':
        case 'expeditions':
        case 'destination':
        case 'destinations':
            return 'task';
        case 'mission':
        case 'missions':
        case 'interval':
        case 'intervals':
            return 'activity';
        default:
            return type;
    }
};

export const getDestinationParentContent = (destinationType) => {
    switch (destinationType) {
        case 'destination':
            return 'expedition';
        case 'interval':
            return 'mission';
        case 'microburst_resource':
            return 'microburst';
        default:
            throw Error(`Invalid destinationType: ${destinationType}`);
    }
};

export const getTaskGrandparentContent = (taskType) => {
    switch (taskType) {
        case 'task':
            return 'expedition';
        case 'activity':
            return 'mission';
        default:
            throw Error(`Invalid taskType: ${taskType}`);
    }
};

export const getTaskParentContent = (taskType) => {
    switch (taskType) {
        case 'task':
            return 'destination';
        case 'activity':
            return 'interval';
        default:
            throw Error(`Invalid taskType: ${taskType}`);
    }
};

export const addSchoolAdminIfHasTeacher = roles => {
    if (roles.includes('teacher') && !roles.includes('school_admin')) {
        roles.push('school_admin');
    }
    return roles;
};

/**
 * Must called with parent's context. e.g. debounce.call(this, ...)
 */
export function debounce(fn, ms) {
    if (this.debounceTimeoutId) {
        clearTimeout(this.debounceTimeoutId);
    }
    this.debounceTimeoutId = setTimeout(fn, ms);
}

export function debounceAndCancelRequest(fn, ms) {
    if (this.debounceTimeoutId) {
        this.cancelTokenSource.cancel('Request cancelled');
        clearTimeout(this.debounceTimeoutId);
    }
    this.cancelTokenSource = axios.CancelToken.source();
    this.debounceTimeoutId = setTimeout(() => fn(this.cancelTokenSource.token), ms);
}

const getRandomInt = (startInt = 0, len = 1) => Math.floor((Math.random() * (len - startInt)) + startInt);
const getLowerAlphabet = () => String.fromCharCode(getRandomInt(97, 123));
const getUpperAlphabet = () => String.fromCharCode(getRandomInt(65, 91));
const getInt = () => String.fromCharCode(getRandomInt(48, 58));
const getAlphanum = () => [getLowerAlphabet, getUpperAlphabet, getInt][getRandomInt(0, 3)]();
export const generateAlphanum = (len) => {
    let result = [];
    for (let i = 0; i < len; ++i) {
        result.push(getAlphanum());
    }
    return result.join('');
};

/**
 * https://stackoverflow.com/a/29304414
 */
export const generateCSVFile = (data, fileName, mimeType) => {
    let csvContent = '';
    data.forEach((infoArray, index) => {
        const dataString = infoArray.join(',');
        csvContent += index < data.length ? dataString + '\n' : dataString;
    });

    downloadBlob(csvContent, fileName, mimeType);
};

export const downloadBlob = (blob, fileName, mimeType = 'application/octet-stream') => {
    const a = document.createElement('a');
    if (window.URL && 'download' in a) {
        a.href = window.URL.createObjectURL(new Blob([blob], {type: mimeType}));
        a.setAttribute('download', fileName);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    } else {
        location.href = 'data:application/octet-stream,' + encodeURIComponent(blob);
    }
};

export const readExcelFile = (file) => {
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
        reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);
            try {
                const workbook = XLSX.read(data, {type: 'array', raw: true});
                resolve(XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]]));
            } catch (e) {
                reject('Whoops! We can not read the file you selected. Please select another one.');
            }
        };
        reader.readAsArrayBuffer(file);
    });
};

/**
 * https://stackoverflow.com/a/6274381
 */
export const shuffleArray = arr => {
    const arrCopy = deepcopy(arr);
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    if (arr.length > 1 && isEqual(arr, arrCopy)) {
        return shuffleArray(arrCopy);
    }
    return arr;
};

export const isEqual = (obj1, obj2) => {
    if (isTypedArray(obj1) || isKeyCollection(obj1) ||
        isTypedArray(obj2) || isKeyCollection(obj2)) {
        throw Error('isEqual does not accept TypedArray or Key Collection');
    }
    if (Array.isArray(obj1) && Array.isArray(obj2)) {
        if (obj1.length !== obj2.length) {
            return false;
        }
        for (let i = 0; i < obj1.length; ++i) {
            if (!isEqual(obj1[i], obj2[i])) {
                return false;
            }
        }
        return true;
    } else if (isObject(obj1) && isObject(obj2)) {
        if (Object.keys(obj1).length !== Object.keys(obj2).length) {
            return false;
        }
        for (const k of Object.keys(obj1)) {
            if (!isEqual(obj1[k], obj2[k])) {
                return false;
            }
        }
        return true;
    }
    return obj1 === obj2;
};

export const deepcopy = obj => {
    if (isTypedArray(obj) || isKeyCollection(obj)) {
        throw Error('deepcopy does not accept TypedArray or Key Collection');
    }
    if (Array.isArray(obj)) {
        const ret = [];
        for (const v of obj) {
            ret.push(deepcopy(v));
        }
        return ret;
    } else if (isObject(obj)) {
        const ret = {};
        for (const [k, v] of Object.entries(obj)) {
            ret[k] = deepcopy(v);
        }
        return ret;
    }
    return obj;
};

export const isSOS = contentType => contentType === 'sos';

export const isModule = contentType => contentType === 'module';

export const isMission = contentType => contentType === 'mission';

export const isExpedition = contentType => contentType === 'expedition';

export const isMicroburst = contentType => contentType === 'microburst';

export const isUnit = contentType => contentType === 'unit';

export const isIPL = contentType => contentType === 'ipl';

export const isCustom = contentType => (contentType.toLowerCase() === 'custom' || contentType.toLowerCase() === 'custom content' || contentType.toLowerCase() === 'custom_content' || contentType.toLowerCase() === 'custom_contents');

export const isCustomOrExpedition = contentType => isCustom(contentType) || isExpedition(contentType);

export const isModuleOrSOS = contentType => isModule(contentType) || isSOS(contentType);

export const isExpeditionOrMission = contentType => isExpedition(contentType) || isMission(contentType);

export const isExpeditionMissionOrMicroburst = contentType =>
    isExpedition(contentType) || isMission(contentType) || isMicroburst(contentType);

export const isExpeditionMissionOrCustom = contentType =>
    isExpedition(contentType) || isMission(contentType) || isCustom(contentType);

export const expeditionSubtypeFormat = (cell) => {
    switch (cell) {
        case 'regular':
            return <Label bsStyle="primary">REGULAR EXPEDITION</Label>;
        case 'career':
            return <Label bsStyle="success">CAREER EXPEDITION</Label>;
        case 'math':
            return <Label bsStyle="warning">MATH EXPEDITION</Label>;
        default:
            return <Label bsStyle="danger">STEM EXPEDITION</Label>;
    }
};

export const microburstSubtypeFormat = (cell) => {
    switch (cell) {
        case 'middle':
            return <Label bsStyle="primary">MIDDLE SCHOOL</Label>;
        default:
            return <Label bsStyle="danger">ELEMENTARY SCHOOL</Label>;
    }
};

export const assignmentGradeItemTypeFormat = (cell) => {
    switch (cell) {
        case 'Rubric':
            return <Label bsStyle="success">Rubric</Label>;
        case 'Pre-test':
            return <Label bsStyle="primary">Pre-test</Label>;
        case 'Post-test':
            return <Label bsStyle="primary">Post-test</Label>;
        case 'Custom':
            return <Label bsStyle="danger">Custom Content</Label>;
        default:
            return <Label bsStyle="primary">Assessment</Label>;
    }
};

export const getNormalizedContentType = (contentOrContentType) => {
    let contentType = contentOrContentType;
    if (isObject(contentOrContentType)) {
        contentType = contentOrContentType.type;
    }
    if (typeof contentType === 'string') {
        return contentType.replace('content_', '');
    }
    return '';
};

export const addContentTypePrefix = (contentType) => {
    if (typeof contentType === 'string') {
        if (contentType.startsWith('content_')) {
            return contentType;
        }
        return `content_${contentType}`;
    }
    return contentType;
};

export const getContentTypePluralForm = (contentType) => {
    contentType = getNormalizedContentType(contentType);
    switch (contentType) {
        case 'expedition':
            return 'expeditions';
        case 'mission':
            return 'missions';
        case 'microburst':
            return 'microbursts';
        case 'module':
            return 'modules';
        case 'unit':
            return 'units';
        case 'sos':
            return 'sos';
        case 'ipl':
            return 'ipls';
        case 'destination':
            return 'destinations';
        case 'interval':
            return 'intervals';
        case 'task':
            return 'tasks';
        case 'activity':
            return 'activities';
        case 'custom':
            return 'custom_contents';
        case 'custom_content':
            return 'custom_contents';
        case 'resource':
            return 'resources';
        default:
            return contentType;
    }
};

export const getContentTypeSingularForm = (contentTypePlural) => {
    contentTypePlural = getNormalizedContentType(contentTypePlural);
    switch (contentTypePlural) {
        case 'expeditions':
            return 'expedition';
        case 'missions':
            return 'mission';
        case 'microbursts':
            return 'microburst';
        case 'modules':
            return 'module';
        case 'units':
            return 'unit';
        case 'sos':
            return 'sos';
        case 'ipl':
            return 'ipls';
        case 'customs':
            return 'custom_content';
        case 'custom_contents':
            return 'custom_content';
        case 'resources':
            return 'resource';
        default:
            return contentTypePlural;
    }
};

export const isAllowedVideo = (nameOrUrl) => {
    const fileExt = nameOrUrl.toString().split('.').slice(-1)[0];
    return ['mp4', 'avi', 'webm', 'ogg', 'flv', 'mov', 'wmv', 'swf'].includes(fileExt);
};

export const getVideoMIMEType = (nameOrUrl) => {
    const ext = nameOrUrl.split('.').slice(-1)[0];
    switch (ext) {
        case 'mp4':
            return 'video/mp4';
        case 'avi':
            return 'video/x-msvideo';
        case 'webm':
            return 'video/webm';
        case 'ogg':
            return 'video/ogg';
        case 'flv':
            return 'video/x-flv';
        case 'mov':
            return 'video/quicktime';
        case 'wmv':
            return 'video/x-ms-wmv';
        case 'swf':
            return 'application/x-shockwave-flash';
        default:
            return '';
    }
};

export const getVideoTrackFile = (nameOrUrl) => `${nameOrUrl.split('.').slice(0, -1).join('.')}.vtt`;

export const insertTexFuncToEditor = (editorState, texRaw) => {
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();
    return EditorState.push(
        editorState,
        Modifier.insertText(contentState, selectionState, `\`${texRaw}\``),
        'insert-characters'
    );
};

export const getFAIconFromMimeType = (mimeType) => {
    if (mimeType.startsWith('video/')) {
        mimeType = 'video';
    } else if (mimeType.startsWith('image/')) {
        mimeType = 'image';
    } else if (mimeType.startsWith('application/')) {
        mimeType = mimeType.split('/').slice(-1)[0];
        mimeType = mimeType.split('.').slice(-1)[0];
    }

    switch (mimeType) {
        case ('pdf'):
            return <i className="fa fa-file-pdf-o" aria-hidden="true"/>;
        case ('video'):
            return <i className="fa fa-video-camera" aria-hidden="true"/>;
        case ('image'):
            return <i className="fa fa-camera" aria-hidden="true"/>;
        case ('document'):
            return <i className="fa fa-file-word-o" aria-hidden="true"/>;
        case ('presentation'):
            return <i className="fa fa-file-powerpoint-o" aria-hidden="true"/>;
        case ('zip'):
            return <i className="fa fa-archive" aria-hidden="true"/>;
        case ('rtf'):
            return <i className="fa fa-file-text" aria-hidden="true"/>;
        default :
            return '';
    }
};

/**
 * Use this function to unescape
 * - mathtex characters
 * - non-html characters
 * - style custom iframes
 */
export const escapeHTML = (html) => {
    if (html && !isEmpty(html) && (html !== '<p></p>') && html.length > 0) {
        return html
            .replaceAll('&amp;', '&')
            .replaceAll('&apos;', '\'')
            .replaceAll('&#x27;', '\'')
            .replaceAll('&#x2F;', '/')
            .replaceAll('&#39;', '\'')
            .replaceAll('&#47;', '/')
            .replaceAll('&#8221;', '"')
            .replaceAll('&#8220;', '"')
            .replaceAll('&#8219;', '\'')
            .replaceAll('&#8218;', '\'')
            .replaceAll('&#96;', '`')
            .replaceAll('&nbsp;', ' ')
            .replaceAll('&quot;', '"')
            .replaceAll('\'', '\'')
            .replaceAll('&lt;', '<')
            .replaceAll('&gt;', '>')
            .replaceAll(' </ iframe', '</iframe')
            .replaceAll('<iframe ', '<iframe style="min-height: 56vh;" ');
    } else {
        return html;
    }
};

export const compileTex = (input) => (
    isEmpty(input) ? '' :
        input.replace(/`[\s\S&]*?`/gi, (match) => {
            try {
                const santinizedTexFunc = escapeHTML(match.slice(1, -1)).replace(/<[\S]+?>/g, '');
                return katex.renderToString(santinizedTexFunc, {displayMode: false});
            } catch (e) {
                if (e instanceof katex.ParseError) {
                    return match;
                }
                throw e;
            }
        })
);

export const answerTypeFormatter = (cell) => {
    if (cell === 'question_short_answer') return <span className="label label-danger">Short Answer</span>;
    else if (cell === 'question_true_false') return <span className="label label-info">True/False</span>;
    else if (cell === 'question_multiple_choice') return <span className="label label-success">Multiple Choice</span>;
    else return <span className="label label-default">Other</span>;
};

export const answerTypeMap = {
    shortAnswer: 'Short Answer',
    multipleChoice: 'Multiple Choice',
    multipleChoiceImage: 'Multiple Choice Image',
};

export const answerOptions = Object.entries(answerTypeMap).map(([k, v]) => ({value: k, text: v}));

const getFieldValues = (state, fields, selector) => {
    if (isEmpty(fields)) {
        return {};
    }
    const ret = {};
    for (let field of fields) {
        ret[field] = selector(state, field);
    }
    return ret;
};

export const makeForm = (form, formName, mapStateToProps, mapDispatchToProps, selectFields = []) => {
    const selector = formValueSelector(formName);
    let newStateToProps;
    if (!mapStateToProps) {
        newStateToProps = (state) => getFieldValues(state, selectFields, selector);
    } else {
        newStateToProps = (state, ownProps) => ({
            ...mapStateToProps(state, ownProps, formName),
            ...getFieldValues(state, selectFields, selector),
        });
    }
    const newDispatchToProps = mapDispatchToProps ?
        (state, ownProps) => mapDispatchToProps(state, ownProps, formName) : null;
    return connect(newStateToProps, newDispatchToProps)(reduxFormWrapper(form, formName));
};

const getAnswersForMultipleChoice = (data) => {
    const answerKeys = Object.keys(data).filter(k => {
        return !(['answerType', 'answer'].includes(k) || !k.startsWith('answer') || k.endsWith('IsRight'));
    });
    const rightAnswerOrder = [];
    for (let i = 0; i < answerKeys.length; ++i) {
        const answerKey = answerKeys[i];
        if (data[`answer${answerKey.slice(-1)}IsRight`]) {
            rightAnswerOrder.push(i);
        }
    }
    return {
        choices: answerKeys.map(k => data[k]).filter(v => !!v),
        answers: rightAnswerOrder,
    };
};

export const makeQuestionPayload = (data) => {
    let {answerType, questionHtml} = data;
    if (questionHtml instanceof EditorState) {
        questionHtml = draftToHtml(convertToRaw(questionHtml.getCurrentContent()));
    }
    if (answerType === 'shortAnswer') {
        return {
            ...data,
            questionHtml,
            answers: {
                answer: [data.answer],
                prefix: data.prefixes ? data.prefixes.split(';').map(prefix => prefix.trim()) : [''],
                suffix: data.suffixes ? data.suffixes.split(';').map(suffix => suffix.trim()) : [''],
            },
        };
    } else if (answerType === 'multipleChoice') {
        const {choices, answers} = getAnswersForMultipleChoice(data);
        return {
            ...data,
            questionHtml,
            choices,
            answers,
        };
    } else if (answerType === 'multipleChoiceImage') {
        const {answers, choices} = getAnswersForMultipleChoice(data);
        let files = {};
        Object.keys(data).forEach(key => {
            if (isObject(data[key]) && key.startsWith('answer') && key.length > 6) {
                files[key] = data[key][0];
                delete data[key];
            }
        });

        return {
            ...data,
            questionHtml,
            answers,
            files,
            choices
        };
    } else {
        throw Error(`Invalid Answer Type: ${answerType} (type: ${typeof answerType}`);
    }
};

export const normalizeQuestionType = (type) => {
    switch (type) {
        case 'shortAnswer':
        case 'question_short_answer':
            return 'shortAnswer';
        case 'longAnswer':
        case 'question_long_answer':
            return 'longAnswer';
        case 'multipleChoice':
        case 'question_multiple_choice':
            return 'multipleChoice';
        case 'multipleChoiceImage':
        case 'question_multiple_choice_image':
            return 'multipleChoiceImage';
        default:
            return type;
    }
};

export const formatQuestionType = (type) => {
    switch (type) {
        case 'shortAnswer':
        case 'question_short_answer':
            return 'Short Answer';
        case 'multipleChoice':
        case 'question_multiple_choice':
            return 'Multiple Choice';
        case 'multipleChoiceImage':
        case 'question_multiple_choice_image':
            return 'Multiple Choice Image';
        default:
            return type;
    }
};

export const getTableNoDataText = (tableInitialized) => tableInitialized ? 'No Data Available' : 'Loading...';

export const getNormalizedAssessments = (contentOrAssignment) => {
    const {preAssessments = [], postAssessments = []} = contentOrAssignment;
    return [
        ...preAssessments.map(assessment => ({id: assessment.id, type: 'pre', name: assessment.name})),
        ...postAssessments.map(assessment => ({id: assessment.id, type: 'post', name: assessment.name})),
    ];
};

export const normalizeAssessmentTimeLimit = (timeLimit) => {
    if (timeLimit && timeLimit.indexOf(':') !== -1) {
        timeLimit = moment(timeLimit, 'HH:mm:ss').diff(moment().startOf('day'), 'seconds');
    }
    return timeLimit;
};

export const isAllCompleted = (itemList) => {
    let allCompleted = true;
    for (const item of itemList) {
        allCompleted = allCompleted && item.isCompleted;
    }
    return allCompleted;
};

export const arrayToIdKeyedObject = (arr, keyName = 'id') => {
    if (!Array.isArray(arr)) {
        return arr;
    }
    const ret = {};
    if (keyName === null) {
        for (const [k, v] of Object.entries(arr)) {
            ret[k] = v;
        }
    } else {
        for (const v of arr) {
            ret[v[keyName]] = v;
        }
    }
    return ret;
};

export const roundIfIsFloat = (num, persistZero = false, digits = 2) => {
    if (isNaN(num) || Number.isInteger(num) || !Number.isFinite(num)) {
        return num;
    }
    let rounded = num.toFixed(digits);
    while (!persistZero && (rounded.endsWith('0') || rounded.endsWith('.'))) {
        rounded = rounded.slice(0, rounded.length - 1);
    }
    return rounded;
};

// accepts integers or floats, and rounds them to the highest integer
// ie) 1.1 -> 2 , 5 -> 5
export const roundFloatUp = (num) => {
    if (isNaN(num) || !Number.isFinite(num)) {
        return 0;
    }
    return Math.ceil(num);
};

export const normalizeAssignmentGradeItems = (progress, completedOnly = false) => {
    let grades;
    for (const [userId, gradeItems] of Object.entries(progress)) {
        const assessmentGradeItems = [];
        let rubricGradeItem;
        let moduleGradeItem;
        let customGradeItem;
        for (const gradeItem of gradeItems) {
            if (gradeItem.type === 'grade_item_assessment') {
                if (!completedOnly || (completedOnly && gradeItem.status === 'completed')) {
                    assessmentGradeItems.push(gradeItem);
                }
            } else if (gradeItem.type === 'grade_item_rubric' && !isEmpty(gradeItem.rubricMarks)) {
                rubricGradeItem = gradeItem;
            } else if (gradeItem.type === 'grade_item_module') {
                moduleGradeItem = gradeItem;
            } else if (gradeItem.type === 'grade_item_custom') {
                customGradeItem = gradeItem;
            }
        }
        grades = {assessmentGradeItems, rubricGradeItem, moduleGradeItem, customGradeItem};
    }
    return grades;
};

export const isAssessmentCompleted = (assessment) => (
    isEmpty(assessment.grades) ? false :
        assessment.grades.reduce((completed, grade) => (
            completed && !isEmpty(grade.status) && grade.status !== 'incomplete'
        ), true)
);


export const getCustomProgress = (assignment) => {
    const customProgress = {...assignment.progress, endDatetime: assignment.endDatetime};
    return customProgress;
};

// custom content should be passed to isContentCompleted iff content type is custom
export const isContentCompleted = (contentProgress, customProgress) => {
    // progress for custom content is saved in the grade item (inside progress, rather than contentProgress).
    if (customProgress) {
        let isCustomContentCompleted = false;

        //if assignment end date passes, mark content as completed
        const currentDate = moment();
        const assignmentEndDate = moment(customProgress.endDatetime);
        if (currentDate.isAfter(assignmentEndDate)) {
            isCustomContentCompleted = true;
            return isCustomContentCompleted;
        }

        for (let studentIndex in customProgress) {
            for (let i = 0; i < customProgress[studentIndex].length; i++) { // iterate through the progress items in the custom assignment (including assessments)
                if (customProgress[studentIndex][i].type === 'grade_item_custom' && customProgress[studentIndex][i].status === 'completed') { // if one logged in student is done, mark as completed
                    isCustomContentCompleted = true;
                }
            }
        }
        return isCustomContentCompleted;
        // for all other non-custom assignments
    } else {
        if (isEmpty(contentProgress)) {
            return false;
        }
        let completed = true;
        for (const progress of Object.values(contentProgress)) {
            completed = completed && (!isEmpty(progress) && progress.completed);
        }
        return completed;
    }
};

export const isAssignmentCompleted = (assignment) => {
    const {content = {}} = assignment;
    const contentType = getNormalizedContentType(content) || getNormalizedContentType(assignment.contentType);
    let customProgress = null;
    if (isCustom(contentType)) {
        customProgress = getCustomProgress(assignment);
    }
    if (!isEmpty(content)) { // this true from AssignmentDetail view for students
        if (contentType === 'module') {
            let isCompleted = !isEmpty(content.moduleProgress);
            for (const progress of content.moduleProgress) {
                isCompleted = isCompleted && (
                    Object.entries(progress)
                        .filter(([k]) => k.endsWith('Complete'))
                        .reduce((completed, [k, v]) => completed && !!v, true)
                );
            }
            return isCompleted;
        } else if (contentType === 'unit') {
            let isCompleted = true;
            for (const iplProgresses of Object.values(assignment.progress)) {
                isCompleted = isCompleted && (
                    iplProgresses
                        .reduce((completed, iplProgress) => completed && iplProgress.status === 'completed', true)
                );
            }
            return isCompleted;
        }
        return (
            assignment.preAssessments.reduce((completed, assessment) => (
                completed && isAssessmentCompleted(assessment)
            ), true) &&
            assignment.postAssessments.reduce((completed, assessment) => (
                completed && isAssessmentCompleted(assessment)
            ), true) &&
            isContentCompleted(assignment.contentProgress, customProgress)
        );
    } else if (assignment.contentType) { // this true in the student assignment table
        const contentType = getNormalizedContentType(assignment.contentType);
        const {contentProgress} = assignment;
        if (contentType === 'module') {
            let isCompleted = !isEmpty(contentProgress);
            for (const progress of Object.values(contentProgress)) {
                isCompleted = isCompleted && !isEmpty(progress) && (
                    Object.entries(progress)
                        .filter(([k]) => k.endsWith('Complete'))
                        .reduce((completed, [k, v]) => completed && !!v, true)
                );
            }
            return isCompleted;
        } else if (contentType === 'unit') {
            let isCompleted = true;
            for (const iplProgresses of Object.values(assignment.progress)) {
                isCompleted = isCompleted && (
                    iplProgresses
                        .reduce((completed, iplProgress) => completed && iplProgress.status === 'completed', true)
                );
            }
            return isCompleted;
        }
        return (
            assignment.preAssessments.reduce((completed, assessment) => (
                completed && isAssessmentCompleted(assessment)
            ), true) &&
            assignment.postAssessments.reduce((completed, assessment) => (
                completed && isAssessmentCompleted(assessment)
            ), true) &&
            isContentCompleted(contentProgress, customProgress) // customProgress is undefined unless content type is custom
        );
    }
};

export const sumArr = (arr) => arr.reduce((total, num) => total + num, 0);

export const moduleGradeItemSubtypeMap = {
    pretest: 'Module Guide',
    posttest: 'Module Post Test',
    rca2: 'RCA2',
    rca3: 'RCA3',
    rca4: 'RCA4',
    rca5: 'RCA5',
    pa1: 'PA1',
    pa2: 'PA2',
    pa3: 'PA3',
    participation: 'Participation',
    attendance: 'Attendance',
};

export const moduleGradePointsAvailableMap = {
    pretest: 10,
    posttest: 100,
    rca2: 30,
    rca3: 30,
    rca4: 30,
    rca5: 30,
    pa1: 20,
    pa2: 20,
    pa3: 20,
};


export const concatStudentNames = (users) => {
    if (!isEmpty(users)) {
        let studentNames = '';
        if (users.length === 1) {
            studentNames = users[0].firstName + ' ' + users[0].lastName;
        } else if (users.length === 2) {
            studentNames = users[0].firstName + ' ' + users[0].lastName + ' and ' + users[1].firstName + ' ' + users[1].lastName;
        } else {
            users.map((user, key) => {
                if (key !== 0) {
                    studentNames = studentNames + ',';
                }
                studentNames = studentNames + ' ' + user.firstName + ' ' + user.lastName;
            });
        }
        return studentNames;
    } else {
        return 'Hello';
    }
};

// Used for converting decimal color values from XML files into HEX values that can be used for colors.
export const decimaltoHex = function (decimal = 0) {
    decimal = Number(decimal);
    if (decimal < 0 || decimal > 16777215) { // Maximum hex of #FFFFFF = 16777215 in decimal.
        throw Error(`Decimal value ${decimal} is out of range.`);
    }
    const hex = decimal.toString(16);
    /* Hex padding - when converting from decimal to hex, we can get a hex value of "FF00" which is actually the
    incorrect result/color. If we pad the result with 0's to ensure a 6 digit hex then it looks like we get right
    color. */
    return ('000000'.substr(0, 6 - hex.length) + hex).toUpperCase();
};

/* reference for bgcolor, bgalpha, and alpha
    alpha seems to be in this format: alpha="0.25"
    bgcolor is in the decimal format: bgcolor="16777215"
    bgalpha is an integer between 0 (dark) and 100 (transparent) */
export const RGBFormatFromDecimal = function (decimal, bgalpha = '') {
    const hexColor = decimaltoHex(decimal);

    // bgalpha seems to be represented by 3 digits in the XML file.
    // i.e.) bgalpha is 100, when it should be 1 -  We change it here:
    if (bgalpha > 1) {
        bgalpha = bgalpha / 100;
    }

    if (bgalpha || bgalpha === 0) {
        bgalpha = Number(bgalpha);
        if (bgalpha < 0 || bgalpha > 1) {
            throw Error('bgalpha value is out of range.');
        }
    } else {
        bgalpha = 1;
    }

    const redColorRGB = parseInt(hexColor.substring(0, 2), 16);
    const greenColorRGB = parseInt(hexColor.substring(2, 4), 16);
    const blueColorRGB = parseInt(hexColor.substring(4, 6), 16);

    if (redColorRGB < 0 || redColorRGB > 255) {
        throw Error('RBG red value is out of range.');
    }
    if (greenColorRGB < 0 || greenColorRGB > 255) {
        throw Error('RBG green value is out of range.');
    }
    if (blueColorRGB < 0 || blueColorRGB > 255) {
        throw Error('RBG blue value is out of range.');
    }

    return `rgba(${redColorRGB}, ${greenColorRGB}, ${blueColorRGB}, ${bgalpha})`;
};

export const isOdd = function (x) {
    return x & 1;
};

export const isDivisibleByFour = function (x) {
    return (parseInt(x) + 1) % 4 === 0;
};

export const createImage = (url) => (
    new Promise((resolve) => {
        const img = document.createElement('img');
        img.onload = () => resolve(img);
        img.setAttribute('src', url);
    })
);

export const createMicroburstCertificateBlob = ({
                                                    img,
                                                    studentFullName,
                                                    schoolName,
                                                    teacherFullName,
                                                    startDatetime,
                                                    endDatetime,
                                                }) => {
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const context = canvas.getContext('2d');
    context.drawImage(img, 0, 0);
    const dataUrl = canvas.toDataURL('image/png');

    const width = 792;
    const height = 612;
    const pdfGenerator = pdfMake.createPdf({
        pageSize: {
            width,
            height,
        },
        pageMargins: [0, 0, 0, 0],
        pageOrientation: 'landscape',
        content: [
            {
                image: dataUrl,
                fit: [width, height],
            },
            {
                layout: 'noBorders',
                absolutePosition: {x: 410, y: 180},
                table: {
                    widths: [340],
                    heights: [90],
                    body: [
                        [{
                            text: studentFullName,
                            style: {
                                fontSize: 30,
                                color: '#fff',
                            },
                            alignment: 'center',
                        }]
                    ],
                },
            },
            {
                table: {
                    widths: [340],
                    heights: [40],
                    body: [
                        [{
                            text: `${schoolName},`,
                            style: {
                                fontSize: 20,
                            },
                            alignment: 'center',
                        }]
                    ],

                },
                layout: 'noBorders',
                absolutePosition: {x: 410, y: 255},
            },
            {
                table: {
                    widths: [340],
                    heights: [40],
                    body: [
                        [{
                            text: `Academic Year ${momentDatetime(startDatetime).format('YYYY')} - ${
                                momentDatetime(endDatetime).format('YYYY')}`,
                            style: {
                                fontSize: 20,

                            },
                            alignment: 'center',
                        }]
                    ],

                },
                layout: 'noBorders',
                absolutePosition: {x: 410, y: 280},
            },
            {
                table: {
                    widths: [260],
                    heights: [40],
                    body: [
                        [{
                            text: `${teacherFullName}, Program Instructor`,
                            style: {
                                fontSize: 13,
                                letterSpacing: '-0.1px',
                                wordSpacing: '-0.1px',
                                color: '#555',
                                fontWeight: 500
                            },
                            alignment: 'right',
                        }]
                    ],

                },
                layout: 'noBorders',
                absolutePosition: {x: 480, y: 550},
            },
        ],
    });
    return new Promise((resolve) => {
        pdfGenerator.getBlob((blob) => resolve({blob}));
    });
};

// sorts an array of objects by 2 attributes: lastName, firstName descending
export const sortByLastnameFirstname = (a, b) => {
    let lastNameA = a.lastName.toUpperCase();
    let lastNameB = b.lastName.toUpperCase();
    let firstNameA = a.firstName.toUpperCase();
    let firstNameB = b.firstName.toUpperCase();
    return ((lastNameA < lastNameB) ? -1 : (lastNameA > lastNameB) ? 1
        : ((firstNameA < firstNameB) ? -1 : (firstNameA > firstNameB) ? 1
            : 0));
};

// sorts an array of objects by some attribute, descending
export const sortByAttrAlphebetical = (attribute) => (a, b) => {
    let textA = a[attribute].toUpperCase();
    let textB = b[attribute].toUpperCase();
    return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
};

export const sortByKey = (array, key) => {
    return array.sort(function (a, b) {
        let x, y;
        x = a[key];
        y = b[key];
        return ((x < y) ? -1 : ((x > y) ? 1 : 0));
    });
};



// WEBPACK FOOTER //
// ./src/js/utilities/common.js